import { useState } from 'react';
import { Archive, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { invalidateMapInit } from '@/lib/map/mapInitCache';
import { cascadeForFarm, pluralFields } from '../structureLogic';
import type { FarmWithAggregate } from '../types';
import { useArchiveFarm } from '../hooks/useStructureQueries';

interface ArchiveFarmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  farm: FarmWithAggregate | null;
}

/**
 * Архивация хозяйства с confirm-предупреждением о каскаде. Считает «скроется
 * N полей / M посевов» из данных (cascadeForFarm). Бэк каскадит те же
 * сущности (archive_farm → fields → sowings). Обратимо в БД; restore за
 * capability-флагом (AK-344).
 */
export function ArchiveFarmDialog({
  open,
  onOpenChange,
  farm,
}: ArchiveFarmDialogProps) {
  const archive = useArchiveFarm();
  const [error, setError] = useState<string | undefined>();

  if (!farm) return null;

  const cascade = cascadeForFarm(farm);

  const submit = async () => {
    setError(undefined);
    try {
      await archive.mutateAsync(farm.id);
      // Поля хозяйства скрылись с карты — сбрасываем chip-кеш /map.
      invalidateMapInit();
      toast.success(`Хозяйство «${farm.name}» архивировано`);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError('Не удалось архивировать хозяйство');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Заархивировать «{farm.name}»?</DialogTitle>
          <DialogDescription>
            Хозяйство скроется из структуры, карты и списков. Данные сохранятся —
            можно восстановить.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-1.5 text-sm text-muted-foreground">
          <li>
            • Скроется{' '}
            <span className="font-medium text-foreground">
              {cascade.fields} {pluralFields(cascade.fields)}
            </span>
            {cascade.sowings > 0 && (
              <>
                {' '}и{' '}
                <span className="font-medium text-foreground">
                  {cascade.sowings} посевов
                </span>
              </>
            )}{' '}
            (каскад)
          </li>
          <li>• История операций и осмотров сохраняется</li>
          <li>• Появится под фильтром «Показать архивные»</li>
          <li>• Восстановление — в один клик</li>
        </ul>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={archive.isPending}
          >
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={archive.isPending}
          >
            {archive.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Архивирую…
              </>
            ) : (
              <>
                <Archive className="mr-1.5 h-4 w-4" />
                Архивировать
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
